if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      this.initThumbnailArrows();
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    initThumbnailArrows() {
      this.thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
      this.thumbnailPrev = this.elements.thumbnails.querySelector('button[name="previous"]');
      this.thumbnailNext = this.elements.thumbnails.querySelector('button[name="next"]');
      if (!this.thumbnailList || !this.thumbnailPrev || !this.thumbnailNext) return;

      this.thumbnailPrev.addEventListener('click', this.scrollThumbnails.bind(this, -1));
      this.thumbnailNext.addEventListener('click', this.scrollThumbnails.bind(this, 1));
      this.thumbnailList.addEventListener('scroll', this.updateThumbnailArrows.bind(this), { passive: true });
      // Also fires on load and when the layout switches between column and row.
      new ResizeObserver(this.updateThumbnailArrows.bind(this)).observe(this.thumbnailList);
    }

    // The thumbnails are a column next to the main image on desktop and a row under
    // it on mobile, so the scroll axis follows whatever the CSS lays out.
    getThumbnailAxis() {
      if (getComputedStyle(this.thumbnailList).flexDirection === 'column') {
        return { start: 'top', scroll: 'scrollTop', scrollSize: 'scrollHeight', size: 'clientHeight', offset: 'offsetTop', itemSize: 'offsetHeight' };
      }
      return { start: 'left', scroll: 'scrollLeft', scrollSize: 'scrollWidth', size: 'clientWidth', offset: 'offsetLeft', itemSize: 'offsetWidth' };
    }

    updateThumbnailArrows() {
      const axis = this.getThumbnailAxis();
      const position = this.thumbnailList[axis.scroll];
      const maxPosition = this.thumbnailList[axis.scrollSize] - this.thumbnailList[axis.size];
      const canScrollPrev = position > 1;
      const canScrollNext = position < maxPosition - 1;

      this.thumbnailPrev.disabled = !canScrollPrev;
      this.thumbnailNext.disabled = !canScrollNext;
      this.elements.thumbnails.classList.toggle('can-scroll-prev', canScrollPrev);
      this.elements.thumbnails.classList.toggle('can-scroll-next', canScrollNext);
    }

    scrollThumbnails(direction) {
      const items = this.thumbnailList.querySelectorAll('.thumbnail-list__item');
      if (items.length < 2) return;

      // Advances a page minus one thumbnail, so the last one seen stays as a reference.
      const axis = this.getThumbnailAxis();
      const step = items[1][axis.offset] - items[0][axis.offset];
      const thumbnailsPerClick = Math.max(1, Math.floor(this.thumbnailList[axis.size] / step) - 1);
      const currentIndex = Math.round(this.thumbnailList[axis.scroll] / step);
      this.thumbnailList.scrollTo({ [axis.start]: (currentIndex + direction * thumbnailsPerClick) * step });
    }

    scrollThumbnailIntoView(thumbnail) {
      if (!this.thumbnailList) return;

      const axis = this.getThumbnailAxis();
      const start = thumbnail[axis.offset];
      const end = start + thumbnail[axis.itemSize];
      const visibleStart = this.thumbnailList[axis.scroll];
      const visibleEnd = visibleStart + this.thumbnailList[axis.size];
      if (start >= visibleStart && end <= visibleEnd) return;

      this.thumbnailList.scrollTo({ [axis.start]: start - (this.thumbnailList[axis.size] - thumbnail[axis.itemSize]) / 2 });
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          // Measured against the list itself: offsetLeft counts from the media wrapper,
          // so it also includes the thumbnail column on desktop.
          const mediaList = activeMedia.parentElement;
          const mediaStart = activeMedia.getBoundingClientRect().left - mediaList.getBoundingClientRect().left;
          mediaList.scrollTo({ left: mediaList.scrollLeft + mediaStart });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      this.scrollThumbnailIntoView(thumbnail);
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
